// ─────────────────────────────────────────────
// Oike Diagnostic — transcript cleaning
// Used by the Diagnostic tab (preview of the length) and by diagnostic-admin.mts
// (the version that is stored). Pure functions: no I/O.
// Handles Zoom (VTT and .txt) and Google Meet transcripts pasted as text.
// ─────────────────────────────────────────────
import { TRANSCRIPT_LIMITS } from './diagnosticConfig.js';

const TS = '\\d{1,2}:\\d{2}(?::\\d{2})?(?:[.,]\\d{1,3})?';
const CUE_TIMING = new RegExp(`^${TS}\\s*-->\\s*${TS}.*$`);
const TS_ALONE = new RegExp(`^[\\[(]?${TS}[\\])]?$`);
const TS_LEADING = new RegExp(`^[\\[(]?${TS}[\\])]?\\s*[-–—:]?\\s+`);
const TS_INLINE = new RegExp(`\\s*[\\[(]${TS}[\\])]\\s*`, 'g');
const ZOOM_SPEAKER_HEADER = new RegExp(`^\\[([^\\]]{1,80})\\]\\s*${TS}$`); // "[Ana Pérez] 10:01:05"
const SPEAKER = /^([^:\n]{1,60}?):\s+(.+)$/;

function isSpeakerName(name) {
  const words = name.trim().split(/\s+/);
  return words.length <= 6 && !/https?$|[?!.]$/.test(name) && /\p{L}/u.test(name);
}

/**
 * Remove timestamps and cue numbers, merge consecutive lines of the same
 * speaker and normalize spaces. Returns the clean text.
 */
export function cleanTranscript(raw) {
  const lines = String(raw ?? '')
    .replace(/\r\n?/g, '\n')
    .replace(/[​﻿]/g, '')
    .replace(/[ \t]/g, ' ')
    .split('\n');

  const blocks = []; // { speaker, parts }
  let headerSpeaker = null;
  const push = (speaker, text) => {
    const last = blocks[blocks.length - 1];
    if (last && (speaker === null || last.speaker === speaker)) last.parts.push(text);
    else blocks.push({ speaker, parts: [text] });
  };

  for (let line of lines) {
    line = line.trim();
    if (!line) continue;
    if (/^WEBVTT\b/i.test(line) || /^NOTE\b/.test(line) || /^\d+$/.test(line) || CUE_TIMING.test(line) || TS_ALONE.test(line)) continue;
    const header = line.match(ZOOM_SPEAKER_HEADER);
    if (header) { headerSpeaker = header[1].trim(); continue; }

    line = line
      .replace(/^<v\s+([^>]+)>/i, '$1: ')       // VTT voice tag
      .replace(/<\/?[a-z][^>]*>/gi, '')          // other VTT tags
      .replace(TS_LEADING, '')
      .replace(TS_INLINE, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!line) continue;

    const m = line.match(SPEAKER);
    if (m && isSpeakerName(m[1])) { headerSpeaker = null; push(m[1].trim(), m[2].trim()); }
    else if (headerSpeaker) push(headerSpeaker, line);
    else push(null, line);
  }

  return blocks
    .map(b => (b.speaker ? `${b.speaker}: ` : '') + b.parts.join(' '))
    .join('\n\n');
}

/**
 * Split the clean text into the two Airtable fields.
 * Returns [main, cont] or null when it is longer than the total limit.
 */
export function splitTranscript(clean, limits = TRANSCRIPT_LIMITS) {
  const { fieldMax, totalMax } = limits;
  if (clean.length > totalMax) return null;
  if (clean.length <= fieldMax) return [clean, ''];
  // Cut between paragraphs when possible, so that both parts fit.
  const minCut = clean.length - fieldMax;
  let cut = clean.lastIndexOf('\n', fieldMax);
  if (cut < minCut) cut = clean.lastIndexOf(' ', fieldMax);
  if (cut < minCut) cut = fieldMax;
  return [clean.slice(0, cut), clean.slice(cut)];
}
